import { useCallback, useEffect, useRef, useState } from 'react';
import { InfiniteScrollOptions, InfiniteScrollReturn } from './useInfiniteScroll.types';
import { useWindowScroll } from './useWindowScroll';

/**
 * Хук для реализации бесконечной подгрузки данных при скролле
 * @param onLoadMore - Функция для загрузки следующей порции данных
 * @param options - Опции для настройки поведения хука
 * @returns Объект с состоянием и методами для управления бесконечной подгрузкой
 */
export const useInfiniteScroll = (
    onLoadMore: () => void | Promise<void>,
    options: InfiniteScrollOptions = {},
): InfiniteScrollReturn => {
    const { threshold = 100, enabled = true, hasMore: hasMoreProp, loading: loadingProp = false } = options;

    const [loading, setLoading] = useState(loadingProp);
    const [hasMore] = useState(hasMoreProp);
    const containerRef = useRef<HTMLDivElement>(null);
    const isLoadingRef = useRef(false);

    // Функция для проверки, нужно ли загружать данные
    const checkIfShouldLoad = useCallback(() => {
        if (!enabled || !hasMore || loading || isLoadingRef.current) {
            return false;
        }

        const container = containerRef.current;

        if (!container) {
            // Если контейнер не задан, используем window
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;

            return scrollTop + windowHeight >= documentHeight - threshold;
        }

        // Если контейнер задан, проверяем его скролл
        const { scrollTop, scrollHeight, clientHeight } = container;

        return scrollTop + clientHeight >= scrollHeight - threshold;
    }, [enabled, hasMore, loading, threshold]);

    // Обработчик загрузки данных
    const loadMore = useCallback(async () => {
        if (!enabled || !hasMore || !hasMoreProp || loading || isLoadingRef.current) {
            return;
        }

        isLoadingRef.current = true;
        setLoading(true);

        try {
            await onLoadMore();
        } catch (error) {
            console.error('Error loading more data:', error);
        } finally {
            setLoading(false);
            isLoadingRef.current = false;
        }
    }, [enabled, hasMore, loading, onLoadMore, hasMoreProp]);

    const handleScroll = useCallback(() => {
        if (checkIfShouldLoad()) {
            loadMore();
        }
    }, [checkIfShouldLoad, loadMore]);

    useWindowScroll(handleScroll, 100, { passive: true });

    // Синхронизация внешних пропсов с внутренним состоянием
    useEffect(() => {
        // console.log('loadingProp', loadingProp)
        setLoading(loadingProp);
    }, [loadingProp]);

    // useEffect(() => {
    //     setHasMore(hasMoreProp);
    // }, [hasMoreProp]);

    // Проверка при первом рендере
    // useEffect(() => {
    //     if (enabled && hasMore && !loading) {
    //         // Минимальная задержка для рендеринга DOM
    //         const timeoutId = setTimeout(() => {
    //             if (checkIfShouldLoad()) {
    //                 // loadMore();
    //             }
    //         }, 0);

    //         return () => clearTimeout(timeoutId);
    //     }
    // }, [enabled, hasMore, loading, checkIfShouldLoad, loadMore]);

    return {
        loading,
        loadMore,
        hasMore,
        containerRef,
    };
};
